import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { type ExecutionLogFilters } from '../types';

import {
  type ExecutionLogResponse,
  zodExecutionLogResponseSchema,
} from './validations';

const DEFAULT_LIMIT = 100;

/**
 * Fetches the data subscription execution log (latest version of each run) with
 * optional filters.
 *
 * @param filters - Status, trigger-source, contact-point, DLN and pagination filters.
 * @param options - Optional request options (e.g., abort signal).
 * @returns The paginated execution log.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription
 */
export const fetchExecutionLog = async (
  filters: ExecutionLogFilters = {},
  options?: KyRequestOptions,
): Promise<ExecutionLogResponse> => {
  const searchParams: Record<string, number | string> = {
    limit: filters.limit ?? DEFAULT_LIMIT,
    offset: filters.offset ?? 0,
    orderBy: 'created_on',
    sortOrder: 'DESC',
  };

  if (filters.taskStatus) {
    searchParams.taskStatus = filters.taskStatus;
  }

  if (filters.triggerSource) {
    searchParams.triggerSource = filters.triggerSource;
  }

  if (filters.dataSubscriptionContactPointId) {
    searchParams.dataSubscriptionContactPointId =
      filters.dataSubscriptionContactPointId;
  }

  if (filters.dlnId) {
    searchParams.dlnId = filters.dlnId;
  }

  if (filters.dlnNr) {
    searchParams.dlnNr = filters.dlnNr;
  }

  const response = await vestigasApi
    .get(ENDPOINT.DATA_SUBSCRIPTION.GET_EXECUTION_LOG(), {
      ...options,
      searchParams,
    })
    .json<ExecutionLogResponse>();

  const validated = zodExecutionLogResponseSchema.safeParse(response);

  if (!validated.success) {
    console.warn(
      '[fetchExecutionLog] Response validation failed',
      validated.error,
    );
    return response;
  }

  return validated.data;
};
